import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { PieChart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { energyColors } from '@/components/dashboard/charts/chartConfig';
import { EnergyMixBreakdown } from '@/types/reports';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { formatEnergy } from '@/lib/energyCalculations';

interface EnergyMixDonutProps {
  energyMix: EnergyMixBreakdown | null;
  title?: string;
}

export function EnergyMixDonut({
  energyMix,
  title = 'Energy Source Mix',
}: EnergyMixDonutProps) {
  const isMobile = useIsMobile();

  // Empty state
  if (!energyMix || (energyMix.solarKwh === 0 && energyMix.gridKwh === 0 && energyMix.generatorKwh === 0)) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <PieChart className="h-4 w-4 text-[#8FB560]" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[220px] flex items-center justify-center">
          <div className="text-center">
            <PieChart className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No energy data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Note: Battery not shown separately - Solar → Battery → Load is one path
  const series = [energyMix.solarKwh, energyMix.gridKwh, energyMix.generatorKwh];
  const labels = ['Solar', 'Grid', 'Generator'];
  const colors = [energyColors.solar, energyColors.grid, energyColors.generator];

  const totalKwh = energyMix.solarKwh + energyMix.gridKwh + energyMix.generatorKwh;
  const totalFormatted = formatEnergy(totalKwh);

  const options: ApexOptions = {
    chart: {
      type: 'donut',
      background: 'transparent',
    },
    labels,
    colors,
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '14px',
              color: '#9ca3af',
            },
            value: {
              show: true,
              fontSize: '20px',
              fontWeight: 600,
              color: '#fff',
              formatter: (val: string) => {
                const num = parseInt(val, 10);
                const formatted = formatEnergy(num);
                return formatted.value;
              },
            },
            total: {
              show: true,
              label: `Total ${totalFormatted.unit}`,
              color: '#9ca3af',
              formatter: () => totalFormatted.value,
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (_val: number, opts) => {
        const percents = [energyMix.solarPercent, energyMix.gridPercent, energyMix.generatorPercent];
        const percent = percents[opts.seriesIndex];
        // Hide label if segment is too small
        if (percent < 5) return '';
        return `${percent}%`;
      },
      style: {
        fontSize: isMobile ? '14px' : '14px',
        fontWeight: 600,
        colors: ['#fff'],
      },
      dropShadow: {
        enabled: true,
        top: 1,
        left: 1,
        blur: 2,
        opacity: 0.5,
      },
    },
    legend: {
      show: true,
      position: 'bottom',
      labels: {
        colors: '#9ca3af',
      },
      markers: {
        offsetX: -4,
      },
      itemMargin: {
        horizontal: isMobile ? 4 : 8,
      },
      fontSize: isMobile ? '10px' : '14px',
      formatter: (seriesName: string, opts) => {
        const idx = opts.seriesIndex;
        const kwhs = [energyMix.solarKwh, energyMix.gridKwh, energyMix.generatorKwh];
        const formatted = formatEnergy(kwhs[idx]);
        const subFontSize = isMobile ? '10px' : '14px';
        return `<div style="display: flex; flex-direction: column; align-items: center; line-height: 1.3;"><span>${seriesName}</span><span style="font-size: ${subFontSize};">${formatted.value} ${formatted.unit}</span></div>`;
      },
    },
    stroke: {
      show: false,
    },
    tooltip: {
      theme: 'dark',
      custom: function({ seriesIndex, w }) {
        const kwhs = [energyMix.solarKwh, energyMix.gridKwh, energyMix.generatorKwh];
        const percents = [energyMix.solarPercent, energyMix.gridPercent, energyMix.generatorPercent];
        const label = w.globals.labels[seriesIndex];
        const formatted = formatEnergy(kwhs[seriesIndex]);
        const color = w.globals.colors[seriesIndex];
        return `<div class="px-3 py-2 rounded-lg" style="background: #1f2937;">
          <div class="flex items-center gap-2">
            <span class="w-3 h-3 rounded-full" style="background: ${color};"></span>
            <span class="font-medium text-white">${label}</span>
          </div>
          <div class="text-sm text-gray-300 mt-1">
            <span class="font-semibold text-white">${formatted.value} ${formatted.unit}</span>
            <span class="text-gray-400 ml-1">(${percents[seriesIndex]}%)</span>
          </div>
        </div>`;
      },
    },
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          <PieChart className="h-4 w-4 text-[#8FB560]" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex items-center justify-center min-h-0 px-0 pt-0 pb-4 md:p-6 md:pt-0">
        <div className={`w-full h-full ${isMobile ? 'min-h-[250px]' : 'min-h-[200px]'}`}>
          <Chart
            type="donut"
            options={options}
            series={series}
            height={isMobile ? 250 : '100%'}
            width="100%"
          />
        </div>
      </CardContent>
    </Card>
  );
}
